import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { type CommunicationStateRow, useCommunicationState } from "@features/communications/api";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { Alert, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useMemo } from "react";

const CommunicationOutagesPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const { selectedObject, setSelectedObject } = useGlobalObject(objects, { required: false });

  const stateQuery = useCommunicationState({
    region: selectedObject?.regionId,
    object: selectedObject?.objectId,
    limit: 500,
  });

  const data = useMemo(() => {
    if (!stateQuery.data) {
      return [];
    }
    return stateQuery.data.filter((row) => row.connection_state === 0);
  }, [stateQuery.data]);

  const columns: ColumnsType<CommunicationStateRow> = [
    { title: "Счётчик", dataIndex: "meter_id", width: 140 },
    {
      title: "Объект",
      dataIndex: "object_name",
      width: 220,
      render: (_value, row) =>
        row.object_name ? `${row.object_name} (№${row.object_id ?? "?"})` : "—",
    },
    {
      title: "Канал связи",
      dataIndex: "channel_name",
      width: 220,
      render: (_value, row) => row.channel_name ?? row.port_name ?? "—",
    },
    {
      title: "Телефон / IP",
      dataIndex: "host",
      width: 220,
      render: (_value, row) => row.host ?? row.phone ?? "—",
    },
    {
      title: "Нет связи с",
      dataIndex: "connection_since",
      width: 200,
      render: (value?: string) =>
        value ? <Tag color="red">{dayjs(value).format("DD.MM.YYYY HH:mm")}</Tag> : <Tag>—</Tag>,
    },
    {
      title: "Последний ответ",
      dataIndex: "last_poll_at",
      width: 200,
      render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Ошибки",
      dataIndex: "comm_errors",
      width: 160,
      render: (_value, row) => (
        <Space size={4}>
          <Tag color="red">{row.comm_errors ?? 0}</Tag>
          <Tag color="orange">{row.protocol_errors ?? 0}</Tag>
        </Space>
      ),
    },
  ];

  return (
    <PageLayoutWithHelp title="Недоступные каналы связи">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Список счётчиков, по которым связь отсутствует. Используйте для оперативного реагирования
          и устранения проблем.
        </Typography.Text>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              allowClear
              placeholder="Объект (все доступные)"
              style={{ minWidth: 320 }}
            />
          </FilterRow>
        </FilterBar>

        {!stateQuery.isLoading && data.length === 0 ? (
          <Alert type="success" showIcon title="Все каналы в норме" />
        ) : (
          <EnterpriseTable<CommunicationStateRow>
            rowKey={(row) =>
              String(
                row.meter_id ??
                  row.place_id ??
                  `${row.object_id ?? "0"}-${row.feeder_id ?? "0"}-${row.channel_name ?? ""}-${
                    row.host ?? ""
                  }-${row.tcp_port ?? ""}-${row.phone ?? ""}`,
              )
            }
            columns={columns}
            dataSource={data}
            loading={stateQuery.isLoading}
            scroll={{ x: 1000 }}
            size="small"
            toolbar={{ reload: true, onReload: () => stateQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default CommunicationOutagesPage;
